// src/pages/ApplicationChampionshipSeasonEdit/index.tsx
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Text, useToast } from '@chakra-ui/react';
import { useQueries } from '@tanstack/react-query';

import { PageContent } from '@/components/PageContent';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useSeasonById } from '@/services/queries/season';
import {
  usePostStagesBatchMutation,
  usePostStageBatchTeamsMutation,
  usePutStageMutation,
  useStages,
  useDeleteStageTeamMutation,
} from '@/services/queries/stage';
import { useSeasonTeams, usePostSeasonTeamsBatchMutation, useDeleteSeasonTeamMutation } from '@/services/queries/season-team';
import { GetParamsObject } from '@/model/query-params';
import { SeasonType, Stage, StageType } from '@/model';
import { PutStageRequest } from '@/services/request/stage';
import { StageService } from '@/services/instances/stage';
import { StageConverter } from '@/converters/stage';
import { buildStagesBatchItems } from '../ApplicationChampionshipSeason/shared/utils/buildStagesBatchItems';
import { TeamLite } from '../ApplicationChampionshipSeason/shared/steps/Step3Teams/types';

import { breadcrumb } from './breadcrumb';
import { seasonFormSchema, SeasonFormData } from '../ApplicationChampionshipSeason/shared/schemas';
import { DEFAULT_GROUPS_COUNT, DEFAULT_STANDINGS_COLUMNS } from '../ApplicationChampionshipSeason/shared/constants';
import { useStageSeeding } from '../ApplicationChampionshipSeason/shared/hooks/useStageSeeding';
import { SeasonWizardTabs, WizardStep } from '../ApplicationChampionshipSeason/shared/components/SeasonWizardTabs';
import { Step1SeasonInfo } from '../ApplicationChampionshipSeason/shared/steps/Step1Info/Step1SeasonInfo';
import { Step2SeasonStages } from '../ApplicationChampionshipSeason/shared/steps/Step2Stages/Step2SeasonStages';
import { Step3SeasonTeams } from '../ApplicationChampionshipSeason/shared/steps/Step3Teams/Step3SeasonTeams';
import { Step4SeasonReview } from '../ApplicationChampionshipSeason/shared/steps/Step4Review/Step4SeasonReview';

const SEASONS_LIST_ROUTE = '/application/championships/seasons';

type DirtyStagePut = {
  id: string;
  payload: PutStageRequest;
};

/**
 * Compara o estado atual do form com as stages originais carregadas do banco e
 * monta o payload de PUT só para as stages cujo `name` ou `group_name` mudou.
 * Mantém todos os demais campos (type, stage_order, is_main_widget, …) iguais
 * ao original — o PUT exige o objeto completo.
 */
const computeDirtyStagePuts = (values: SeasonFormData, originalById: Map<string, Stage>): DirtyStagePut[] => {
  if (!values.season_uuid) return [];
  const seasonUuid = values.season_uuid;
  const dirty: DirtyStagePut[] = [];

  // Nome de zona vazio/whitespace → null (backend mantém NULL = label default).
  const toLabel = (value?: string | null): string | null => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  };

  const pushIfDirty = (
    stageUuid: string | undefined,
    nextName: string,
    nextGroupName: string | null,
    nextQualifiers?: {
      direct_qualifiers_count: number;
      playoff_qualifiers_count: number;
      secondary_qualifiers_count: number;
      relegated_count: number;
      direct_qualifiers_label: string | null;
      playoff_qualifiers_label: string | null;
      secondary_qualifiers_label: string | null;
      relegated_qualifiers_label: string | null;
    },
  ) => {
    if (!stageUuid) return;
    const original = originalById.get(stageUuid);
    if (!original) return;

    const originalGroupName = original.group_name ?? null;
    const nameChanged = nextName !== original.name;
    const groupNameChanged = nextGroupName !== originalGroupName;

    const qualifiers = nextQualifiers ?? {
      direct_qualifiers_count: original.direct_qualifiers_count ?? 0,
      playoff_qualifiers_count: original.playoff_qualifiers_count ?? 0,
      secondary_qualifiers_count: original.secondary_qualifiers_count ?? 0,
      relegated_count: original.relegated_count ?? 0,
      direct_qualifiers_label: original.direct_qualifiers_label ?? null,
      playoff_qualifiers_label: original.playoff_qualifiers_label ?? null,
      secondary_qualifiers_label: original.secondary_qualifiers_label ?? null,
      relegated_qualifiers_label: original.relegated_qualifiers_label ?? null,
    };
    const qualifiersChanged =
      qualifiers.direct_qualifiers_count !== (original.direct_qualifiers_count ?? 0) ||
      qualifiers.playoff_qualifiers_count !== (original.playoff_qualifiers_count ?? 0) ||
      qualifiers.secondary_qualifiers_count !== (original.secondary_qualifiers_count ?? 0) ||
      qualifiers.relegated_count !== (original.relegated_count ?? 0);
    const labelsChanged =
      qualifiers.direct_qualifiers_label !== (original.direct_qualifiers_label ?? null) ||
      qualifiers.playoff_qualifiers_label !== (original.playoff_qualifiers_label ?? null) ||
      qualifiers.secondary_qualifiers_label !== (original.secondary_qualifiers_label ?? null) ||
      qualifiers.relegated_qualifiers_label !== (original.relegated_qualifiers_label ?? null);

    if (!nameChanged && !groupNameChanged && !qualifiersChanged && !labelsChanged) return;

    dirty.push({
      id: stageUuid,
      payload: {
        season_uuid: seasonUuid,
        name: nextName,
        type: original.type,
        is_main_widget: original.is_main_widget,
        stage_order: original.stage_order,
        current_round: original.current_round ?? null,
        group_name: nextGroupName,
        direct_qualifiers_count: qualifiers.direct_qualifiers_count,
        playoff_qualifiers_count: qualifiers.playoff_qualifiers_count,
        secondary_qualifiers_count: qualifiers.secondary_qualifiers_count,
        relegated_count: qualifiers.relegated_count,
        direct_qualifiers_label: qualifiers.direct_qualifiers_label,
        playoff_qualifiers_label: qualifiers.playoff_qualifiers_label,
        secondary_qualifiers_label: qualifiers.secondary_qualifiers_label,
        relegated_qualifiers_label: qualifiers.relegated_qualifiers_label,
      },
    });
  };

  values.group_stages.forEach(item => {
    pushIfDirty(item.uuid, 'Fase de Grupos', item.group_name.trim(), {
      direct_qualifiers_count: item.direct_qualifiers_count ?? 0,
      playoff_qualifiers_count: item.playoff_qualifiers_count ?? 0,
      secondary_qualifiers_count: item.secondary_qualifiers_count ?? 0,
      relegated_count: item.relegated_count ?? 0,
      direct_qualifiers_label: toLabel(item.direct_qualifiers_label),
      playoff_qualifiers_label: toLabel(item.playoff_qualifiers_label),
      secondary_qualifiers_label: toLabel(item.secondary_qualifiers_label),
      relegated_qualifiers_label: toLabel(item.relegated_qualifiers_label),
    });
  });

  values.knockout_stages.forEach(item => {
    pushIfDirty(item.uuid, item.name.trim(), null);
  });

  // Fase única (LEAGUE) — cutoffs e nomes de zona para sincronizar.
  if (values.league_stage_uuid) {
    const leagueOriginal = originalById.get(values.league_stage_uuid);
    if (leagueOriginal) {
      pushIfDirty(values.league_stage_uuid, leagueOriginal.name, leagueOriginal.group_name ?? null, {
        direct_qualifiers_count: values.league_stage_config?.direct_qualifiers_count ?? 0,
        playoff_qualifiers_count: values.league_stage_config?.playoff_qualifiers_count ?? 0,
        secondary_qualifiers_count: values.league_stage_config?.secondary_qualifiers_count ?? 0,
        relegated_count: values.league_stage_config?.relegated_count ?? 0,
        direct_qualifiers_label: toLabel(values.league_stage_config?.direct_qualifiers_label),
        playoff_qualifiers_label: toLabel(values.league_stage_config?.playoff_qualifiers_label),
        secondary_qualifiers_label: toLabel(values.league_stage_config?.secondary_qualifiers_label),
        relegated_qualifiers_label: toLabel(values.league_stage_config?.relegated_qualifiers_label),
      });
    }
  }

  return dirty;
};

export const ApplicationChampionshipSeasonEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const [tabIndex, setTabIndex] = useState(0);

  const { data: season, isLoading, isError, error } = useSeasonById(id ?? '');

  // Carrega as stages reais da temporada (até 200) para:
  //   - decidir se mudar type/groups está locked no Step 1
  //   - popular o form (Step 2) com as fases vindas do banco
  const stagesParams = useMemo(
    () => GetParamsObject({ season_uuid: id ?? '' } as Record<string, string>, 1, 200, 'asc', 'stage_order'),
    [id],
  );
  const { data: stagesData } = useStages(id ? stagesParams : undefined);
  const realStages = useMemo(() => stagesData?.data ?? [], [stagesData]);
  const hasStages = realStages.length > 0;

  // Carrega season_teams já cadastrados nesta temporada para popular o Step 3.
  const seasonTeamsParams = useMemo(
    () => GetParamsObject({ season_uuid: id ?? '' } as Record<string, string>, 1, 200, 'asc', 'created_at'),
    [id],
  );
  const { data: seasonTeamsData } = useSeasonTeams(id ? seasonTeamsParams : undefined);
  const existingSeasonTeams = useMemo(() => seasonTeamsData?.data ?? [], [seasonTeamsData]);

  // Para cada stage do tipo GROUP, busca os times associados (com uuid do
  // StageTeam — necessário para DELETE individual no diff do Edit).
  const groupStageUuids = useMemo(
    () => realStages.filter(stage => stage.type === StageType.GROUP).map(stage => stage.uuid),
    [realStages],
  );

  const stageTeamsQueries = useQueries({
    queries: groupStageUuids.map(stageUuid => ({
      queryKey: ['GET_STAGE_TEAMS_FOR_GROUP', stageUuid],
      queryFn: async () => {
        const params = GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'created_at');
        const [err, data] = await StageService.getStageTeams(stageUuid, params);
        if (err) throw err;
        return {
          stageUuid,
          teams: StageConverter.toDomainFromStageTeamsResponse(data),
        };
      },
      enabled: !!stageUuid,
      refetchOnWindowFocus: false,
    })),
  });

  const allStageTeamsLoaded = groupStageUuids.length === 0 || stageTeamsQueries.every(q => q.data !== undefined || q.isError);

  // stageUuid → Array<{ stageTeamUuid, teamUuid }> (preserva uuid do StageTeam
  // para o algoritmo de diff no handleConfirm do Edit).
  const stageTeamsByStageUuid = useMemo(() => {
    const map = new Map<string, Array<{ stageTeamUuid: string; teamUuid: string }>>();
    stageTeamsQueries.forEach(q => {
      if (q.data) {
        map.set(
          q.data.stageUuid,
          q.data.teams.map(stageTeam => ({
            stageTeamUuid: stageTeam.uuid,
            teamUuid: stageTeam.team_uuid ?? stageTeam.team?.uuid ?? '',
          })),
        );
      }
    });
    return map;
    // stageTeamsQueries é um novo array a cada render; usar JSON estável é caro.
    // Em vez disso, deps no comprimento + sinal de completude evita ciclos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStageTeamsLoaded, groupStageUuids.length]);

  // Metadados (uuid → TeamLite) extraídos das season_teams para abastecer o
  // cache do Step 3 em modo Edit.
  const initialTeamsMetadata = useMemo(() => {
    const map: Record<string, TeamLite> = {};
    existingSeasonTeams.forEach(seasonTeam => {
      const teamUuid = seasonTeam.team_uuid ?? seasonTeam.team?.uuid;
      if (!teamUuid || !seasonTeam.team) return;
      map[teamUuid] = {
        uuid: teamUuid,
        name: seasonTeam.team.name,
        acronym: seasonTeam.team.acronym,
        logo_path: seasonTeam.team.logo_path,
      };
    });
    return map;
  }, [existingSeasonTeams]);

  const postStagesBatch = usePostStagesBatchMutation();
  const putStage = usePutStageMutation();
  const postSeasonTeamsBatch = usePostSeasonTeamsBatchMutation();
  const deleteSeasonTeam = useDeleteSeasonTeamMutation();
  const postStageBatchTeams = usePostStageBatchTeamsMutation();
  const deleteStageTeam = useDeleteStageTeamMutation();
  const [isSavingRenames, setIsSavingRenames] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mapa uuid → Stage original (snapshot do backend) para detectar dirty fields.
  const originalById = useMemo(() => {
    const map = new Map<string, Stage>();
    realStages.forEach(stage => map.set(stage.uuid, stage));
    return map;
  }, [realStages]);

  const form = useForm<SeasonFormData>({
    defaultValues: {
      step: 1,
      season_uuid: id,
      championship_uuid: '',
      season_year: '',
      season_type: undefined,
      is_filtered: false,
      is_active: true,
      groups_count: DEFAULT_GROUPS_COUNT,
      group_stages: [],
      knockout_stages: [],
      league_stage_config: {
        direct_qualifiers_count: 0,
        playoff_qualifiers_count: 0,
        secondary_qualifiers_count: 0,
        relegated_count: 0,
        direct_qualifiers_label: '',
        playoff_qualifiers_label: '',
        secondary_qualifiers_label: '',
        relegated_qualifiers_label: '',
      },
      standings_columns: DEFAULT_STANDINGS_COLUMNS,
    },
    resolver: zodResolver(seasonFormSchema),
    mode: 'onSubmit',
  });

  // Quando há stages reais, não queremos que o seeding sobrescreva com defaults.
  useStageSeeding(form, !hasStages);

  // Aguarda Season + Stages + SeasonTeams + StageTeams (de todos os grupos)
  // carregarem antes de popular o form de uma vez só. Senão dá race com a UI
  // do Step 3 (que lê o cache de metadados na inicialização).
  useEffect(() => {
    if (!season || !stagesData) return;
    if (id && !seasonTeamsData) return;
    if (!allStageTeamsLoaded) return;

    const groupStageItems = realStages
      .filter(stage => stage.type === StageType.GROUP)
      .map(stage => ({
        uuid: stage.uuid,
        group_name: stage.group_name ?? '',
        team_uuids: stageTeamsByStageUuid.get(stage.uuid)?.map(item => item.teamUuid) ?? [],
        direct_qualifiers_count: stage.direct_qualifiers_count ?? 0,
        playoff_qualifiers_count: stage.playoff_qualifiers_count ?? 0,
        secondary_qualifiers_count: stage.secondary_qualifiers_count ?? 0,
        relegated_count: stage.relegated_count ?? 0,
        direct_qualifiers_label: stage.direct_qualifiers_label ?? '',
        playoff_qualifiers_label: stage.playoff_qualifiers_label ?? '',
        secondary_qualifiers_label: stage.secondary_qualifiers_label ?? '',
        relegated_qualifiers_label: stage.relegated_qualifiers_label ?? '',
      }));

    const knockoutStageItems = realStages
      .filter(stage => stage.type === StageType.KNOCKOUT)
      .map(stage => ({ uuid: stage.uuid, name: stage.name }));

    const seasonTeamItems = existingSeasonTeams.map(seasonTeam => ({
      uuid: seasonTeam.uuid,
      team_uuid: seasonTeam.team_uuid ?? seasonTeam.team?.uuid ?? '',
    }));

    const leagueStage = realStages.find(stage => stage.type === StageType.LEAGUE);

    form.reset({
      step: 1,
      season_uuid: season.uuid,
      championship_uuid: season.championship?.uuid ?? season.championship_uuid,
      season_year: String(season.year),
      season_type: season.type,
      is_filtered: season.is_filtered,
      is_active: season.is_active,
      standings_columns: season.standings_columns ?? DEFAULT_STANDINGS_COLUMNS,
      groups_count: groupStageItems.length > 0 ? groupStageItems.length : DEFAULT_GROUPS_COUNT,
      group_stages: groupStageItems,
      knockout_stages: knockoutStageItems,
      season_teams: seasonTeamItems,
      league_stage_uuid: leagueStage?.uuid,
      league_stage_config: {
        direct_qualifiers_count: leagueStage?.direct_qualifiers_count ?? 0,
        playoff_qualifiers_count: leagueStage?.playoff_qualifiers_count ?? 0,
        secondary_qualifiers_count: leagueStage?.secondary_qualifiers_count ?? 0,
        relegated_count: leagueStage?.relegated_count ?? 0,
        direct_qualifiers_label: leagueStage?.direct_qualifiers_label ?? '',
        playoff_qualifiers_label: leagueStage?.playoff_qualifiers_label ?? '',
        secondary_qualifiers_label: leagueStage?.secondary_qualifiers_label ?? '',
        relegated_qualifiers_label: leagueStage?.relegated_qualifiers_label ?? '',
      },
    });
  }, [
    season,
    stagesData,
    realStages,
    seasonTeamsData,
    existingSeasonTeams,
    allStageTeamsLoaded,
    stageTeamsByStageUuid,
    id,
    form,
  ]);

  const handleCancel = () => navigate(SEASONS_LIST_ROUTE);

  const handleConfirm = async () => {
    form.setValue('step', 0);
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();

    if (!values.season_uuid) {
      toast({
        status: 'error',
        title: t('pages.application_season.components.season_step1.toasts.missing_season_id'),
      });
      return;
    }

    const seasonUuid = values.season_uuid;

    // Temporada sem stages — fluxo idêntico ao Create: cria stages, depois
    // season_teams, depois distribui em grupos (HYBRID).
    if (!hasStages) {
      setIsSubmitting(true);
      try {
        const stagesItems = buildStagesBatchItems(values);
        const createdStages = await postStagesBatch.mutateAsync({
          season_uuid: seasonUuid,
          items: stagesItems,
        });

        const seasonTeamUuids = values.season_teams.map(item => item.team_uuid);
        if (seasonTeamUuids.length > 0) {
          await postSeasonTeamsBatch.mutateAsync({
            season_uuid: seasonUuid,
            team_uuids: seasonTeamUuids,
          });
        }

        if (values.season_type === SeasonType.HYBRID) {
          const createdGroupStages = createdStages.filter(stage => stage.type === StageType.GROUP);
          await Promise.all(
            values.group_stages.map(async (groupItem, index) => {
              const stageUuid = createdGroupStages[index]?.uuid;
              if (!stageUuid || groupItem.team_uuids.length === 0) return;
              await postStageBatchTeams.mutateAsync({
                id: stageUuid,
                payload: { team_uuids: groupItem.team_uuids },
              });
            }),
          );
        }

        toast({
          status: 'success',
          title: t('pages.application_season.components.season_step3.toasts.create_success'),
        });
        navigate(SEASONS_LIST_ROUTE);
      } catch (error: unknown) {
        toast({
          status: 'error',
          title: (error as Error)?.message || t('pages.application_season.components.season_step3.toasts.create_error'),
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Temporada já tem stages — algoritmo de diffs:
    //   1) Renames de stages (PUTs em paralelo)
    //   2) season_teams: POST batch (adds) + DELETE individuais (removes)
    //   3) stage_teams por grupo: POST batch (adds) + DELETE individuais (removes)
    const dirtyStagePuts = computeDirtyStagePuts(values, originalById);

    // season_teams diff (compara team_uuid, mas guarda uuid do SeasonTeam para DELETE)
    const originalSeasonTeamByTeamUuid = new Map<string, string>();
    existingSeasonTeams.forEach(seasonTeam => {
      const teamUuid = seasonTeam.team_uuid ?? seasonTeam.team?.uuid;
      if (teamUuid) originalSeasonTeamByTeamUuid.set(teamUuid, seasonTeam.uuid);
    });
    const currentSeasonTeamUuids = new Set(values.season_teams.map(item => item.team_uuid));

    const seasonTeamsToAdd = [...currentSeasonTeamUuids].filter(uuid => !originalSeasonTeamByTeamUuid.has(uuid));
    const seasonTeamsToRemove: string[] = []; // SeasonTeam uuids
    originalSeasonTeamByTeamUuid.forEach((seasonTeamUuid, teamUuid) => {
      if (!currentSeasonTeamUuids.has(teamUuid)) {
        seasonTeamsToRemove.push(seasonTeamUuid);
      }
    });

    // stage_teams por grupo diff
    type StageTeamsBatchAdd = { stageUuid: string; teamUuids: string[] };
    const stageTeamsToAddBatches: StageTeamsBatchAdd[] = [];
    const stageTeamsToRemove: string[] = []; // StageTeam uuids

    values.group_stages.forEach(groupItem => {
      if (!groupItem.uuid) return;
      const originalEntries = stageTeamsByStageUuid.get(groupItem.uuid) ?? [];
      const originalByTeamUuid = new Map<string, string>(); // teamUuid → stageTeamUuid
      originalEntries.forEach(({ stageTeamUuid, teamUuid }) => {
        originalByTeamUuid.set(teamUuid, stageTeamUuid);
      });
      const currentTeamUuids = new Set(groupItem.team_uuids);

      const teamsToAdd = [...currentTeamUuids].filter(uuid => !originalByTeamUuid.has(uuid));
      if (teamsToAdd.length > 0) {
        stageTeamsToAddBatches.push({
          stageUuid: groupItem.uuid,
          teamUuids: teamsToAdd,
        });
      }

      originalByTeamUuid.forEach((stageTeamUuid, teamUuid) => {
        if (!currentTeamUuids.has(teamUuid)) {
          stageTeamsToRemove.push(stageTeamUuid);
        }
      });
    });

    const nothingChanged =
      dirtyStagePuts.length === 0 &&
      seasonTeamsToAdd.length === 0 &&
      seasonTeamsToRemove.length === 0 &&
      stageTeamsToAddBatches.length === 0 &&
      stageTeamsToRemove.length === 0;

    if (nothingChanged) {
      toast({
        status: 'success',
        title: t('pages.application_season.components.season_edit.toasts.edit_success'),
      });
      navigate(SEASONS_LIST_ROUTE);
      return;
    }

    setIsSavingRenames(true);
    try {
      // 1) Renames de stages.
      if (dirtyStagePuts.length > 0) {
        await Promise.all(dirtyStagePuts.map(({ id: stageId, payload }) => putStage.mutateAsync({ id: stageId, payload })));
      }

      // 2) season_teams.
      if (seasonTeamsToAdd.length > 0) {
        await postSeasonTeamsBatch.mutateAsync({
          season_uuid: seasonUuid,
          team_uuids: seasonTeamsToAdd,
        });
      }
      if (seasonTeamsToRemove.length > 0) {
        await Promise.all(seasonTeamsToRemove.map(uuid => deleteSeasonTeam.mutateAsync(uuid)));
      }

      // 3) stage_teams.
      if (stageTeamsToAddBatches.length > 0) {
        await Promise.all(
          stageTeamsToAddBatches.map(batch =>
            postStageBatchTeams.mutateAsync({
              id: batch.stageUuid,
              payload: { team_uuids: batch.teamUuids },
            }),
          ),
        );
      }
      if (stageTeamsToRemove.length > 0) {
        await Promise.all(stageTeamsToRemove.map(uuid => deleteStageTeam.mutateAsync(uuid)));
      }

      toast({
        status: 'success',
        title: t('pages.application_season.components.season_edit.toasts.edit_success'),
      });
      navigate(SEASONS_LIST_ROUTE);
    } catch (error: unknown) {
      toast({
        status: 'error',
        title: (error as Error)?.message || t('pages.application_season.components.season_edit.toasts.rename_error'),
      });
    } finally {
      setIsSavingRenames(false);
    }
  };

  if (isError) {
    return (
      <PageContent title={t('pages.application_season.components.season_edit.page_title')} breadcrumbItems={breadcrumb}>
        <Text color='red.500'>
          {error instanceof Error ? error.message : t('pages.application_season.components.season_edit.errors.load_failed')}
        </Text>
      </PageContent>
    );
  }

  const steps: WizardStep[] = [
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_info'),
      content: <Step1SeasonInfo mode='edit' isTypeLocked={hasStages} onAdvance={() => setTabIndex(1)} onCancel={handleCancel} />,
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_stages'),
      content: (
        <Step2SeasonStages
          onBack={() => setTabIndex(0)}
          onAdvance={() => setTabIndex(2)}
          onCancel={handleCancel}
          isReadOnly={hasStages}
        />
      ),
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_teams'),
      content: (
        <Step3SeasonTeams
          onBack={() => setTabIndex(1)}
          onAdvance={() => setTabIndex(3)}
          onCancel={handleCancel}
          initialMetadata={initialTeamsMetadata}
        />
      ),
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_review'),
      content: (
        <Step4SeasonReview
          onBack={() => setTabIndex(2)}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting || isSavingRenames}
          primaryLabel={hasStages ? t('pages.application_season.components.season_wizard.footer.close') : undefined}
        />
      ),
    },
  ];

  return (
    <PageContent title={t('pages.application_season.components.season_edit.page_title')} breadcrumbItems={breadcrumb}>
      <FormProvider {...form}>
        <SeasonWizardTabs steps={steps} tabIndex={tabIndex} furthestStep={3} onTabChange={setTabIndex} />
      </FormProvider>

      <CenteredSpinner isLoading={isLoading} />
    </PageContent>
  );
};
